import { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ViewColumnsConfig } from "@redotech/effect-api-shared/rpc/view-folder";
import type {
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
} from "@tanstack/react-table";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  useEncodedSupportTicketFilterGroup,
  useQueryOptions,
} from "../filters/filters-context";
import {
  usePrefetchSupportTicket,
  useSupportTickets,
} from "../hooks/use-support-ticket-data";
import { useSupportTicketTableSelection } from "../hooks/use-support-ticket-table-selection";
import { useSupportTicketsTableColumnState } from "../hooks/use-support-tickets-table-column-state";
import { useSupportV2ViewsAndFolders } from "../hooks/use-support-v2-views-and-folders";
import { DEFAULT_PAGE_SIZE, type SupportTicketColumnDef } from "./types";

interface SupportTicketsTableContextValue {
  data: SupportTicketListItemResponse[];
  columns: SupportTicketColumnDef[];
  isLoading: boolean;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToPage: (page: number) => void;
  focusedSupportTicketId: SupportTicketId | null;
  focusedSupportTicket: SupportTicketListItemResponse | null;
  setFocusedSupportTicketId: (id: SupportTicketId | null) => void;
  selectedSupportTicketId: SupportTicketId | null;
  openSupportTicket: (id?: SupportTicketId | null) => void;
  cancelPendingSupportTicketPrefetch: (id?: SupportTicketId) => void;
  prefetchSupportTicket: (id: SupportTicketId) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  actionMenuOpenForId: SupportTicketId | null;
  setActionMenuOpenForId: (id: SupportTicketId | null) => void;
  columnOrder: ColumnOrderState;
  setColumnOrder: (order: ColumnOrderState) => void;
  columnSizing: ColumnSizingState;
  setColumnSizing: (sizing: ColumnSizingState) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  hideableColumnIds: readonly string[];
  isColumnStateDirty: boolean;
  effectiveColumnsConfig: ViewColumnsConfig | null;
  resetColumnState: () => void;
  excludedSupportTicketIds: SupportTicketId[];
  selectedSupportTicketIds: SupportTicketId[];
  selectedSupportTickets: SupportTicketListItemResponse[];
  selectedSupportTicketCount: number;
  allMatchingSupportTicketsSelected: boolean;
  hasAnySelectedSupportTickets: boolean;
  isSupportTicketSelected: (id: SupportTicketId) => boolean;
  setSupportTicketSelected: (input: {
    supportTicketId: SupportTicketId;
    rowIndex: number;
    checked: boolean;
    shiftKey: boolean;
  }) => void;
  setAllSupportTicketsSelected: (checked: boolean) => void;
  selectAllMatchingSupportTickets: () => void;
  clearSelectedSupportTickets: () => void;
}

/**
 * Safely parse a path/query param as a SupportTicketId. Returns null instead of
 * throwing when the value is missing or not a valid `cnv_<base62>` id, so route
 * segments like `/support-v2/analytics` (which fall through to the dynamic
 * `:supportTicketId` segment) don't crash the table provider.
 */
function parseSupportTicketIdOrNull(
  value: string | null | undefined,
): SupportTicketId | null {
  if (!value) {
    return null;
  }
  try {
    return SupportTicketId.make(value);
  } catch {
    return null;
  }
}

const SupportTicketsTableContext =
  createContext<SupportTicketsTableContextValue | null>(null);

export function useSupportTicketsTableContext(): SupportTicketsTableContextValue {
  const context = useContext(SupportTicketsTableContext);
  if (!context) {
    throw new Error(
      "useSupportTicketsTableContext must be used within a SupportTicketsTableProvider",
    );
  }
  return context;
}

export function useSupportTicketsTableContextMaybe(): SupportTicketsTableContextValue | null {
  return useContext(SupportTicketsTableContext);
}

export const SupportTicketsTableProvider = memo(
  function SupportTicketsTableProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { teamId, supportTicketId: supportTicketIdParam } = useParams<{
      teamId: string;
      supportTicketId: string;
    }>();

    const queryOptions = useQueryOptions();
    const encodedFilterGroup = useEncodedSupportTicketFilterGroup();
    const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage] = useState(0);
    const [prevQueryOptions, setPrevQueryOptions] = useState(queryOptions);
    if (queryOptions !== prevQueryOptions) {
      setPrevQueryOptions(queryOptions);
      if (currentPage !== 0) {
        setCurrentPage(0);
      }
    }

    const setPageSize = useCallback((nextPageSize: number) => {
      setPageSizeState(nextPageSize);
      setCurrentPage(0);
    }, []);

    const { data, totalCount, isLoading } = useSupportTickets(
      currentPage,
      pageSize,
    );
    const { cancelPendingSupportTicketPrefetch, prefetchSupportTicket } =
      usePrefetchSupportTicket();

    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + data.length, totalCount);
    const hasPreviousPage = currentPage > 0;
    const hasNextPage = endIndex < totalCount;

    const goToNextPage = useCallback(() => {
      if (hasNextPage) {
        setCurrentPage((p) => p + 1);
      }
    }, [hasNextPage]);

    const goToPreviousPage = useCallback(() => {
      if (hasPreviousPage) {
        setCurrentPage((p) => p - 1);
      }
    }, [hasPreviousPage]);

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const goToPage = useCallback(
      (page: number) => {
        const clamped = Math.min(Math.max(page, 0), totalPages - 1);
        setCurrentPage(clamped);
      },
      [totalPages],
    );

    const selectedSupportTicketId =
      parseSupportTicketIdOrNull(supportTicketIdParam);

    const focusedSupportTicketId = useMemo<SupportTicketId | null>(() => {
      if (selectedSupportTicketId) {
        return selectedSupportTicketId;
      }
      const searchParams = new URLSearchParams(location.search);
      return parseSupportTicketIdOrNull(searchParams.get("focused"));
    }, [selectedSupportTicketId, location.search]);

    const setFocusedSupportTicketId = useCallback(
      (id: SupportTicketId | null) => {
        if (selectedSupportTicketId) {
          return;
        }
        const searchParams = new URLSearchParams(location.search);
        if (id) {
          searchParams.set("focused", id);
        } else {
          searchParams.delete("focused");
        }
        const newSearch = searchParams.toString();
        navigate(
          { search: newSearch ? `?${newSearch}` : "" },
          { replace: true },
        );
      },
      [selectedSupportTicketId, location.search, navigate],
    );

    const openSupportTicket = useCallback(
      (id?: SupportTicketId | null) => {
        const targetId = id ?? focusedSupportTicketId;
        if (targetId && teamId) {
          navigate(
            `/stores/${teamId}/support-v2/${targetId}${location.search}`,
          );
        }
      },
      [focusedSupportTicketId, teamId, navigate, location.search],
    );

    const [actionMenuOpenForId, setActionMenuOpenForId] =
      useState<SupportTicketId | null>(null);

    const [searchParams] = useSearchParams();
    const activeViewId = searchParams.get("view");
    const { views } = useSupportV2ViewsAndFolders();
    const activeView = useMemo(
      () =>
        activeViewId
          ? (views.find((v) => String(v.id) === activeViewId) ?? null)
          : null,
      [activeViewId, views],
    );

    const {
      columns,
      hideableColumnIds,
      columnOrder,
      setColumnOrder,
      columnSizing,
      setColumnSizing,
      columnVisibility,
      setColumnVisibility,
      isColumnStateDirty,
      effectiveColumnsConfig,
      resetColumnState,
    } = useSupportTicketsTableColumnState(activeView, activeViewId);

    const {
      excludedSupportTicketIds,
      selectedSupportTicketIds,
      selectedSupportTickets,
      selectedSupportTicketCount,
      allMatchingSupportTicketsSelected,
      hasAnySelectedSupportTickets,
      isSupportTicketSelected,
      setSupportTicketSelected,
      setAllSupportTicketsSelected,
      selectAllMatchingSupportTickets,
      clearSelectedSupportTickets,
    } = useSupportTicketTableSelection(data, totalCount, encodedFilterGroup);

    const focusedSupportTicket = useMemo(() => {
      if (!focusedSupportTicketId) {
        return null;
      }
      return data.find((c) => c.id === focusedSupportTicketId) ?? null;
    }, [data, focusedSupportTicketId]);

    const navigateUp = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedSupportTicketId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedSupportTicketId(data[data.length - 1].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedSupportTicketId,
      );
      if (currentIndex <= 0) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedSupportTicketId(data[currentIndex - 1].id);
    }, [data, focusedSupportTicketId, setFocusedSupportTicketId]);

    const navigateDown = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedSupportTicketId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedSupportTicketId(data[0].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedSupportTicketId,
      );
      if (currentIndex === -1 || currentIndex >= data.length - 1) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedSupportTicketId(data[currentIndex + 1].id);
    }, [data, focusedSupportTicketId, setFocusedSupportTicketId]);

    const value = useMemo<SupportTicketsTableContextValue>(
      () => ({
        data,
        columns,
        isLoading,
        currentPage,
        totalCount,
        pageSize,
        setPageSize,
        hasPreviousPage,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        goToPage,
        focusedSupportTicketId,
        focusedSupportTicket,
        setFocusedSupportTicketId,
        selectedSupportTicketId,
        openSupportTicket,
        cancelPendingSupportTicketPrefetch,
        prefetchSupportTicket,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        setActionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        columnVisibility,
        setColumnVisibility,
        hideableColumnIds,
        isColumnStateDirty,
        effectiveColumnsConfig,
        resetColumnState,
        excludedSupportTicketIds,
        selectedSupportTicketIds,
        selectedSupportTickets,
        selectedSupportTicketCount,
        allMatchingSupportTicketsSelected,
        hasAnySelectedSupportTickets,
        isSupportTicketSelected,
        setSupportTicketSelected,
        setAllSupportTicketsSelected,
        selectAllMatchingSupportTickets,
        clearSelectedSupportTickets,
      }),
      [
        data,
        columns,
        isLoading,
        currentPage,
        totalCount,
        pageSize,
        setPageSize,
        hasPreviousPage,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        goToPage,
        focusedSupportTicketId,
        focusedSupportTicket,
        setFocusedSupportTicketId,
        selectedSupportTicketId,
        openSupportTicket,
        cancelPendingSupportTicketPrefetch,
        prefetchSupportTicket,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        columnVisibility,
        setColumnVisibility,
        hideableColumnIds,
        isColumnStateDirty,
        effectiveColumnsConfig,
        resetColumnState,
        excludedSupportTicketIds,
        selectedSupportTicketIds,
        selectedSupportTickets,
        selectedSupportTicketCount,
        allMatchingSupportTicketsSelected,
        hasAnySelectedSupportTickets,
        isSupportTicketSelected,
        setSupportTicketSelected,
        setAllSupportTicketsSelected,
        selectAllMatchingSupportTickets,
        clearSelectedSupportTickets,
      ],
    );

    return (
      <SupportTicketsTableContext.Provider value={value}>
        {children}
      </SupportTicketsTableContext.Provider>
    );
  },
);
